<template>
	<article class="moment-card" :id="`moment-${moment.id}`" :data-date="momentDate">
		<header class="moment-head">
			<div class="author">
				<img class="author-avatar" :src="resolvedAuthorAvatar" alt="author">
				<div class="author-meta">
					<span class="author-name">{{ resolvedAuthorName }}</span>
					<span class="publish-time">{{ momentDateTime }}</span>
				</div>
			</div>
			<span class="time-from-now">{{ moment.createTime | dateFromNow }}</span>
		</header>

		<div class="moment-content typo" v-viewer v-html="renderedContent"></div>

		<footer class="moment-actions">
			<button class="action-btn like-btn" @click="$emit('like', moment.id)">
				<i class="heart icon" :class="liked ? 'liked' : 'outline'"></i>
				<span>{{ moment.likes }}</span>
			</button>
			<button class="action-btn comment-btn" @click="$emit('toggle-comment', moment.id)">
				<i class="comment alternate outline icon"></i>
				<span>{{ commentOpen ? '收起评论' : '评论' }}</span>
				<span class="badge">{{ commentCount }}</span>
			</button>
		</footer>

		<transition name="comment-slide">
			<div v-show="commentOpen" class="comment-container">
				<slot></slot>
			</div>
		</transition>
	</article>
</template>

<script>
const {normalizeMomentContent} = require('@/util/momentContent')

export default {
	name: 'MomentFeedCard',
	props: {
		moment: {
			type: Object,
			required: true
		},
		liked: {
			type: Boolean,
			default: false
		},
		commentOpen: {
			type: Boolean,
			default: false
		},
		commentCount: {
			type: Number,
			default: 0
		},
		defaultAuthorName: {
			type: String,
			default: '博主'
		},
		defaultAuthorAvatar: {
			type: String,
			default: '/img/avatar.jpg'
		}
	},
	computed: {
		resolvedAuthorName() {
			return this.moment.authorName || this.defaultAuthorName
		},
		resolvedAuthorAvatar() {
			return this.moment.authorAvatar || this.defaultAuthorAvatar
		},
		renderedContent() {
			return normalizeMomentContent(this.moment.content)
		},
		momentDate() {
			return this.formatDate(this.moment.createTime)
		},
		momentDateTime() {
			const date = new Date(this.moment.createTime)
			const yyyy = date.getFullYear()
			const mm = String(date.getMonth() + 1).padStart(2, '0')
			const dd = String(date.getDate()).padStart(2, '0')
			const hh = String(date.getHours()).padStart(2, '0')
			const ii = String(date.getMinutes()).padStart(2, '0')
			return `${yyyy}-${mm}-${dd} ${hh}:${ii}`
		}
	},
	methods: {
		formatDate(dateInput) {
			const date = new Date(dateInput)
			const yyyy = date.getFullYear()
			const mm = String(date.getMonth() + 1).padStart(2, '0')
			const dd = String(date.getDate()).padStart(2, '0')
			return `${yyyy}-${mm}-${dd}`
		}
	}
}
</script>

<style scoped>
.moment-card {
	background: #fff;
	border-radius: 20px;
	border: 1px solid #e2ebf3;
	box-shadow: 0 18px 34px rgba(15, 23, 42, .06);
	margin-bottom: 20px;
	overflow: hidden;
}

.moment-head {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 22px 12px;
	border-bottom: 1px solid #f1f5f9;
}

.author {
	display: flex;
	align-items: center;
	gap: 10px;
}

.author-avatar {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	object-fit: cover;
}

.author-meta {
	display: flex;
	flex-direction: column;
}

.author-name {
	font-size: 14px;
	font-weight: 600;
	color: #0f172a;
}

.publish-time {
	font-size: 12px;
	color: #64748b;
}

.time-from-now {
	font-size: 12px;
	color: #6b7280;
}

.moment-content {
	padding: 18px 22px 16px;
	font-size: 16px;
	line-height: 1.95;
	color: #1f2937;
	word-break: break-word;
}

.moment-content :deep(p) {
	margin: 0 0 1.05em;
}

.moment-content :deep(p:last-child) {
	margin-bottom: 0;
}

.moment-content :deep(img) {
	width: auto;
	max-width: min(100%, 430px);
	max-height: 360px;
	object-fit: contain;
	border-radius: 16px;
	display: block;
	margin: 16px auto 18px;
	box-shadow: 0 16px 30px rgba(15, 23, 42, .10);
}

.moment-content :deep(video) {
	width: min(100%, 520px);
	max-width: 100%;
	max-height: 56vh;
	background: #020617;
	border-radius: 18px;
	display: block;
	margin: 18px auto 20px;
	box-shadow: 0 18px 34px rgba(15, 23, 42, .18);
}

.moment-actions {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 18px 16px;
}

.action-btn {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	border: 1px solid #dbe5ef;
	border-radius: 999px;
	padding: 6px 12px;
	background: #fff;
	color: #334155;
	font-size: 12px;
	cursor: pointer;
	transition: all .2s ease;
}

.action-btn:hover {
	border-color: #93c5fd;
	color: #1d4ed8;
	background: #f8fbff;
}

.like-btn .heart.liked {
	color: #e11d48;
}

.comment-btn .badge {
	min-width: 18px;
	padding: 0 5px;
	height: 18px;
	line-height: 18px;
	text-align: center;
	border-radius: 999px;
	background: #e2e8f0;
	color: #334155;
	font-size: 11px;
}

.comment-container {
	border-top: 1px solid #eef2f7;
	background: #fbfdff;
	padding: 12px 14px 14px;
}

.comment-slide-enter-active,
.comment-slide-leave-active {
	transition: max-height .25s ease, opacity .2s ease, padding .2s ease;
	max-height: 520px;
	opacity: 1;
}

.comment-slide-enter,
.comment-slide-leave-to {
	max-height: 0;
	opacity: 0;
	padding-top: 0;
	padding-bottom: 0;
}

@media (max-width: 768px) {
	.moment-card {
		border-radius: 16px;
	}

	.moment-head {
		padding: 14px 14px 10px;
	}

	.moment-content {
		padding: 14px 14px 12px;
		font-size: 15px;
		line-height: 1.85;
	}

	.moment-actions {
		padding: 0 12px 14px;
	}

	.comment-container {
		padding: 12px;
	}

	.moment-content :deep(img),
	.moment-content :deep(video) {
		max-width: 100%;
		margin: 14px auto 16px;
	}

	.moment-content :deep(img) {
		max-height: 300px;
	}

	.moment-content :deep(video) {
		max-height: 42vh;
	}
}
</style>
